import { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { usePagination } from "src/hooks/usePagination";
import { useOutsideClick } from "src/hooks/useOutsideClick";
import { SortingState } from "@tanstack/react-table";
import { Transition } from "@headlessui/react";
import Button from "src/features/fields/components/Button";
import Table from "src/features/fields/components/Table";
import POFilter from "src/features/fields/container/POFilter";
import { createColumnHelper } from "@tanstack/react-table";
import moment from "moment";
import FilterIcon from "src/assets/filter.svg";
import { getOrderTypeException } from "src/services/order-api";
import { queryDivisionDetails } from "../../services/division-api";
import { poActions } from "src/context/po-slice";
import { appendParams } from "src/utils/utils";
import OrderTypeExceptionLayout from "./OrderTypeExc-layout.comp";

const OrderTypeException = () => {
    const [loading, setLoading] = useState(false);
    const { orderTypeExceptionTableData, poFiltersData, orderTypeExceptionFilters } = useSelector((state: any) => state.po);
    const { pagination, onPaginationChange, pageIndex, pageSize, resetPagination } = usePagination();
    const [totalRowCount, setTotalRowCount] = useState(10);
    const [isFiltersShown, setIsFiltersShown] = useState<boolean>(false);
    const [sorting, setSorting] = useState<SortingState>();
    const [globalSearch, setGlobalSearch] = useState("");

    const dispatch = useDispatch();

    const ref = useOutsideClick(() => {
        setIsFiltersShown(false);
    });

    const [_, setSearchParams] = useSearchParams();

    const columnHelper = createColumnHelper();
    const columns = [
        columnHelper.accessor("novu_metadata.company.companyName", {
            header: () => "Company Name",
            enableSorting: true,
            cell: (info: any) => info.getValue() || "-",
        }),
        columnHelper.accessor("division_name", {

            header: () => "Division Name",
            enableSorting: true,
            cell: (info: any) => info.row.original?.novu_metadata?.divisions?.[0]?.divisionName || "-",

        }),
        columnHelper.accessor("counter_code", {
            header: () => "Counter Code",
            enableSorting: true,
            cell: (info: any) => info.getValue() || "-",
        }),
        columnHelper.accessor("counter_name", {
            header: () => "Counter Name",
            enableSorting: true,
            cell: (info: any) => info.row.original?.novu_metadata?.counters?.[0]?.counterName || "-",
        }),
        columnHelper.accessor("location_code", {
            header: () => "Location Code",
            enableSorting: true,
            cell: (info: any) => info.getValue() || "-",
        }),
        columnHelper.accessor("location_name", {
            header: () => "Location Name",
            enableSorting: true,
            cell: (info: any) => info.row.original?.novu_metadata?.counters?.[0]?.locationName || "-",
        }),
        columnHelper.accessor("item_code", {
            header: () => "Item Code",
            enableSorting: true,
            cell: (info: any) => info.getValue() || "-",
        }),
        columnHelper.accessor("item_name", {
            header: () => "Item Name",
            enableSorting: true,
            cell: (info: any) => info.row.original?.novu_metadata?.products?.[0]?.product_name || "-",
        }),

        columnHelper.accessor("medvol_code", {
            header: () => "MedVol Code",
            enableSorting: true,
            cell: (info: any) => info.row.original?.novu_metadata?.stockists?.[0]?.medvol_code || "-",
        }),
        columnHelper.accessor("stockist_code", {
            header: () => "Stockist Code",
            enableSorting: true,
            cell: (info: any) => info.getValue() || "-",
        }),
        columnHelper.accessor("stockist_name", {
            header: () => "Stockist Name",
            enableSorting: true,
            cell: (info: any) => info.row.original?.novu_metadata?.stockists?.[0]?.firm_name || "-",
        }),
        columnHelper.accessor("state_name", {
            id: "state_name",
            header: () => "State Name",
            enableSorting: true,
            cell: (info: any) => info.row.original?.novu_metadata?.states?.[0]?.state_name || "-",
        }),
        columnHelper.accessor("created_date", {
            header: () => "Created Date",
            enableSorting: true,
            cell: (info: any) => {
                const date = info.getValue();
                return date
                    ? moment(date).tz("Asia/Kolkata").format("DD MMM YYYY")
                    : "-";
            },
        }),
        columnHelper.accessor("order_type", {
            header: () => "Order Type",
            enableSorting: true,
            cell: (info: any) => info.getValue() || "-",
        }),
        columnHelper.accessor("cndn_remarks_code_int", {
            header: () => "CNDN Remarks",
            enableSorting: true,
            cell: (info: any) => info.getValue() || "-",
        }),
    ];

    const fetchDetails = () => {
        setLoading(true);

        setSearchParams({
            pageSize: pageSize.toString(),
            pageIndex: pageIndex.toString(),
        });

        const companyParams = appendParams(Array.isArray(orderTypeExceptionFilters?.company) ? orderTypeExceptionFilters.company : [], "company", "code");

        const divisionParams = appendParams(
            Array.isArray(orderTypeExceptionFilters?.division) ? orderTypeExceptionFilters.division : [],
            "division",
            "company_divisioncode"
        );

        const stateParams = appendParams(Array.isArray(orderTypeExceptionFilters?.state) ? orderTypeExceptionFilters.state : [], "state", "state_code");

        let filtersQuery = [companyParams, divisionParams, stateParams].join("&");

        filtersQuery.endsWith("&&") && (filtersQuery = filtersQuery.slice(0, -1));
        filtersQuery.endsWith("&") && (filtersQuery = filtersQuery.slice(0, -1));

        getOrderTypeException(
            pageIndex - 1,
            pageSize,
            filtersQuery

        )
            .then((res) => {
                dispatch(poActions.setOrderTypeExceptionTableData(res?.data?.data?.data || []));
                setTotalRowCount(res?.data?.data?.totalDataCount);
            })
            .catch((err) => {
                console.log(err);
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchDetails();
    }, [pageSize, pageIndex, sorting, globalSearch]);

    let pageCount = Math.ceil(totalRowCount / (pageSize ?? 1));
    if (totalRowCount % 10 === 0) {
        pageCount--;
    }

    let timerId: any;
    const handleSearch = (searchQuery: string) => {
        if (timerId) clearTimeout(timerId);

        timerId = setTimeout(() => {
            resetPagination();
            setGlobalSearch(searchQuery);
        }, 500);
    };

    useEffect(() => {
        if (orderTypeExceptionFilters?.company?.length) {
            queryDivisionDetails(`/details/?fields=division_name&company_code=${orderTypeExceptionFilters?.company[0]?.code}`)
                .then((res) => dispatch(poActions.setPoFiltersData({ key: "orderTypeExceptionDivision", value: res.data })))
                .catch((err) => console.log(err));
        } else {
            dispatch(poActions.setPoFiltersData({ key: "orderTypeExceptionDivision", value: [] }));
            dispatch(poActions.setOrderTypeExceptionFilters({ key: "division", value: [] }));
        }

    }, [orderTypeExceptionFilters?.company]);

    return (
        <OrderTypeExceptionLayout>
            <Transition
                as={Fragment}
                show={isFiltersShown}
                enter="transition transition ease-in-out duration-200 transform"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transition  ease-in-out duration-200 transform"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
            >
                <div className="fixed right-0 inset-0 top-0 z-[500]">
                    <POFilter
                        ref={ref}
                        companyDetails={poFiltersData?.company || []}
                        divisionDetails={poFiltersData?.orderTypeExceptionDivision || []}
                        stateDetails={poFiltersData?.state || []}
                        cndnRemarkDetails={poFiltersData?.cndnRemark || []}
                        source="OrderTypeException"
                        onCloseClick={() => setIsFiltersShown(false)}
                    />
                </div>
            </Transition>

            <section>
                <header className="flex px-[3.75rem] items-center justify-between py-5 border-b-[1px] border-b-[#E9E9E9]">
                    <div>
                        <h1 className="text-[#49484C] font-bold text-[2rem]">Order Type Exception Requests</h1>
                        <p className="font-medium text-base text-[#929194]">View and manage all your order type exception requests.</p>
                    </div>

                    <div className="flex items-center gap-2 ">

                        <Button
                            className="relative flex items-center gap-1 px-4 py-3 border"
                            onClick={() => {
                                setIsFiltersShown(true);
                            }}
                        >
                            Filters <img src={FilterIcon} className="w-5 h-5" />
                            {orderTypeExceptionFilters?.filterCount > 0 && (
                                <span className="absolute top-0 right-1 flex items-center justify-center w-5 h-5 text-xs text-white bg-red-500 rounded-full">
                                    {orderTypeExceptionFilters?.filterCount}
                                </span>
                            )}
                        </Button>
                        <Button onClick={fetchDetails} loaderSize="medium"
                            className="bg-[#F3EFFC]  px-4 border-2 text-[#42267F] border-[#7F56D9] ml-auto font-semibold text-base"
                        >
                            Retrieve
                        </Button>
                    </div>
                </header>

                <div className="px-[3.75rem] mt-6">
                    <Table
                        columns={columns}
                        data={orderTypeExceptionTableData ?? []}
                        isLoading={loading}
                        isGlobalSearch={true}
                        manualPagination={true}
                        pageCount={pageCount}
                        pagination={pagination}
                        onPaginationChange={onPaginationChange}
                        sorting={sorting}
                        onSortingChange={setSorting}
                        manualSorting={true}
                        onGlobalSearch={handleSearch}
                    />
                </div>
            </section>
        </OrderTypeExceptionLayout>
    );
};

export default OrderTypeException;
